import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import {
  Box,
  Button,
  TextField,
  Typography,
  Autocomplete
} from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';

import {
  getAddressByZipCode,
  LOAD_ADDRESS_BY_ZIP_CODE
} from 'src/store/actions/clientActions';
import useIsLargeScreen from 'src/hooks/useIsLargeScreen';
import theme from 'src/theme';
import useStyles from '../styles';
import validationSchema from './schema';
import makeInitialState from './initialState';

const StepFour = ({ data, handleNext, handleBack }) => {
  const { classes } = useStyles();
  const initialState = useMemo(() => makeInitialState(data), [data]);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isLargeScreen = useIsLargeScreen();

  const [addressData, setAddressData] = useState({
    cities: [],
    colonias: [],
    states: [],
    isValid: null
  });
  const isLoadingAddress = useSelector(
    isLoadingSelector([LOAD_ADDRESS_BY_ZIP_CODE.action])
  );
  const handleZipCodeChange = useCallback((e, setFieldValue) => {
    const { value } = e.target;
    setFieldValue('equipmentZipCode', value);
    if (value.length < 5) {
      setAddressData({
        cities: [],
        colonias: [],
        states: [],
        isValid: null
      });
    }
    if (value.length === 5) {
      dispatch(getAddressByZipCode(value, setAddressData));
    }
    setFieldValue('equipmentColonia', '');
    setFieldValue('equipmentCity', '');
    setFieldValue('equipmentState', '');
    setFieldValue('equipmentMunicipality', '');
  });

  const onNext = useCallback(
    async values => {
      handleNext({ ...values });
    },
    [handleNext, data]
  );

  const canSubmit = addressData.isValid === true;

  return (
    <Box
      sx={{
        height: {
          xs: 'auto',
          md: '50vh'
        },
        justifyContent: 'space-between',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto'
      }}
    >
      <Box
        sx={{
          height: '10%'
        }}
      >
        <Typography
          variant="h2"
          sx={{
            marginBottom: '10px',
            color: theme.palette.primary.main
          }}
        >
          {t('BUREAU_FORM.STEP_SIX.TITLE')}
        </Typography>
      </Box>
      <Formik
        initialValues={initialState}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={onNext}
      >
        {({
          errors,
          handleSubmit,
          touched,
          values,
          handleChange,
          handleBlur,
          setFieldValue
        }) => {
          const showCopomexError =
            !canSubmit && values.equipmentZipCode?.length === 5;
          return (
            <Box
              onSubmit={handleSubmit}
              component="form"
              sx={{
                height: '90%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
              }}
            >
              <Box className={classes.container}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    marginTop: '10px'
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      width: '100%',
                      margin: '1rem 0 1rem 0',
                      fontWeight: 'bold',
                      fontSize: '16px'
                    }}
                  >
                    {t('BUREAU_FORM.STEP_SIX.RE_USE_ADDRESS_DESCRIPTION')}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between'
                  }}
                >
                  <TextField
                    InputProps={{
                      autoComplete: 'off'
                    }}
                    error={Boolean(
                      touched.equipmentStreet && errors.equipmentStreet
                    )}
                    sx={{
                      width: ' 48%'
                    }}
                    helperText={
                      touched.equipmentStreet && t(errors.equipmentStreet)
                    }
                    label={t('COMMON.STREET')}
                    margin="normal"
                    name="equipmentStreet"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="text"
                    value={values.equipmentStreet}
                    variant="outlined"
                  />

                  <TextField
                    InputProps={{
                      autoComplete: 'off'
                    }}
                    error={Boolean(
                      touched.equipmentExternalHomeNumber &&
                        errors.equipmentExternalHomeNumber
                    )}
                    sx={{
                      width: '24%'
                    }}
                    helperText={
                      touched.equipmentExternalHomeNumber &&
                      t(errors.equipmentExternalHomeNumber)
                    }
                    label={
                      isLargeScreen ? t('COMMON.EXTERNAL_HOME_NUMBER') : 'Ext.'
                    }
                    margin="normal"
                    name="equipmentExternalHomeNumber"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="text"
                    value={values.equipmentExternalHomeNumber}
                    variant="outlined"
                  />

                  <TextField
                    InputProps={{
                      autoComplete: 'off'
                    }}
                    error={Boolean(
                      touched.equipmentInsideHomeNumber &&
                        errors.equipmentInsideHomeNumber
                    )}
                    sx={{
                      width: '24%'
                    }}
                    helperText={
                      touched.equipmentInsideHomeNumber &&
                      t(errors.equipmentInsideHomeNumber)
                    }
                    label={
                      isLargeScreen ? t('COMMON.INSIDE_HOME_NUMBER') : 'Int.'
                    }
                    margin="normal"
                    name="equipmentInsideHomeNumber"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="text"
                    value={values.equipmentInsideHomeNumber}
                    variant="outlined"
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between'
                  }}
                >
                  <TextField
                    InputProps={{
                      autoComplete: 'off'
                    }}
                    error={
                      Boolean(
                        touched.equipmentZipCode && errors.equipmentZipCode
                      ) || showCopomexError
                    }
                    sx={{
                      width: '48%'
                    }}
                    helperText={
                      showCopomexError
                        ? t('ERRORS.ZIP_CODE_WAS_NOT_FOUND')
                        : touched.equipmentZipCode && t(errors.equipmentZipCode)
                    }
                    label={t('COMMON.ZIP_CODE')}
                    margin="normal"
                    name="equipmentZipCode"
                    onBlur={handleBlur}
                    onChange={e => {
                      handleZipCodeChange(e, setFieldValue);
                    }}
                    type="text"
                    value={values.equipmentZipCode}
                    variant="outlined"
                  />

                  <Autocomplete
                    freeSolo
                    options={addressData.cities}
                    value={values.equipmentCity || null}
                    onChange={(event, newValue) => {
                      setFieldValue('equipmentCity', newValue || '');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setFieldValue('equipmentCity', newInputValue);
                    }}
                    disabled={!values.equipmentZipCode || isLoadingAddress}
                    sx={{
                      width: '50%',
                      marginBottom: 2,
                      marginTop: 2
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={
                          isLoadingAddress
                            ? `${t('COMMON.LOADING')} ${t('COMMON.CITY')}`
                            : t('COMMON.CITY')
                        }
                        error={Boolean(
                          touched.equipmentCity && errors.equipmentCity
                        )}
                        helperText={
                          touched.equipmentCity && t(errors.equipmentCity)
                        }
                        onBlur={handleBlur}
                        InputProps={{
                          ...params.InputProps,
                          autoComplete: 'off'
                        }}
                      />
                    )}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between'
                  }}
                >
                  <Autocomplete
                    freeSolo
                    options={addressData.colonias}
                    value={values.equipmentColonia || null}
                    onChange={(event, newValue) => {
                      setFieldValue('equipmentColonia', newValue || '');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setFieldValue('equipmentColonia', newInputValue);
                    }}
                    disabled={!values.equipmentZipCode || isLoadingAddress}
                    sx={{
                      width: '48%',
                      marginBottom: 2,
                      marginTop: 2
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={
                          isLoadingAddress
                            ? `${t('COMMON.LOADING')} ${t('COMMON.COLONIA')}`
                            : t('COMMON.COLONIA')
                        }
                        error={Boolean(
                          touched.equipmentColonia && errors.equipmentColonia
                        )}
                        helperText={
                          touched.equipmentColonia && t(errors.equipmentColonia)
                        }
                        onBlur={handleBlur}
                        InputProps={{
                          ...params.InputProps,
                          autoComplete: 'off'
                        }}
                      />
                    )}
                  />

                  <Autocomplete
                    freeSolo
                    options={addressData.states}
                    value={values.equipmentState || null}
                    onChange={(event, newValue) => {
                      setFieldValue('equipmentState', newValue || '');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setFieldValue('equipmentState', newInputValue);
                    }}
                    disabled={!values.equipmentZipCode || isLoadingAddress}
                    sx={{
                      width: '50%',
                      marginBottom: 2,
                      marginTop: 2
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={
                          isLoadingAddress
                            ? `${t('COMMON.LOADING')} ${t('COMMON.STATE')}`
                            : t('COMMON.STATE')
                        }
                        error={Boolean(
                          touched.equipmentState && errors.equipmentState
                        )}
                        helperText={
                          touched.equipmentState && t(errors.equipmentState)
                        }
                        onBlur={handleBlur}
                        InputProps={{
                          ...params.InputProps,
                          autoComplete: 'off'
                        }}
                      />
                    )}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between'
                  }}
                >
                  <Autocomplete
                    freeSolo
                    options={addressData.municipalities || []}
                    value={values.equipmentMunicipality || null}
                    onChange={(event, newValue) => {
                      setFieldValue('equipmentMunicipality', newValue || '');
                    }}
                    onInputChange={(event, newInputValue) => {
                      setFieldValue('equipmentMunicipality', newInputValue);
                    }}
                    disabled={!values.equipmentZipCode || isLoadingAddress}
                    sx={{
                      width: '48%',
                      marginBottom: 2,
                      marginTop: 2
                    }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label={
                          isLoadingAddress
                            ? `${t('COMMON.LOADING')} ${t(
                                'COMMON.MUNICIPALITY'
                              )}`
                            : t('COMMON.MUNICIPALITY')
                        }
                        error={Boolean(
                          touched.equipmentMunicipality &&
                            errors.equipmentMunicipality
                        )}
                        helperText={
                          touched.equipmentMunicipality &&
                          t(errors.equipmentMunicipality)
                        }
                        onBlur={handleBlur}
                        InputProps={{
                          ...params.InputProps,
                          autoComplete: 'off'
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>

              <Box
                className={classes.box}
                display="flex"
                justifyContent="space-between"
              >
                <Button
                  className={classes.buttons}
                  onClick={() => handleBack()}
                  color="primary"
                  variant="outlined"
                >
                  {t('COMMON.BACK')}
                </Button>
                <Button
                  className={clsx(classes.buttons, classes.buttonMain)}
                  type="submit"
                  color="primary"
                  variant="outlined"
                  disabled={!canSubmit}
                >
                  {t('COMMON.NEXT')}
                </Button>
              </Box>
            </Box>
          );
        }}
      </Formik>
    </Box>
  );
};

StepFour.propTypes = {
  data: PropTypes.object,
  handleNext: PropTypes.func,
  handleBack: PropTypes.func
};

export default StepFour;
